import { msg } from '@lingui/core/macro';
import { Formik } from 'formik';
import _ from 'lodash';
import { getSignUpErrorMessage, SIGN_UP_UNKNOWN_FIELD_ERROR } from 'modules/auth/domain/constants/sign-up-error-messages';
import { AuthSignUpPayload } from 'modules/auth/domain/interfaces/auth-sign-up-payload';
import { authSignUpValidator } from 'modules/auth/domain/validators/auth-sign-up.validator';
import { isOauthAuthorizeSearch, oauthConsentPath } from 'modules/auth/domain/utils/oauth-authorize-request';
import { authSignIn, authSignUp } from 'modules/auth/store/actions';
import { useLocation } from 'react-router-dom';
import {
  authStoreCheckingSelector,
  authStoreCodeRequestedSelector,
  authStoreErrorsSelector,
  authStoreLoadingSelector,
} from 'modules/auth/store/selectors';
import { SPORT_TYPE_LABELS } from 'modules/core/domain/constants/sport-type-labels';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useDispatch, useSelector } from 'react-redux';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';

import { AppDispatch } from 'store/store';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDFormSelect from 'ud-form/components/select';
import { SelectOption } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import { AuthSignInPayload } from 'modules/auth/domain/interfaces/auth-sign-in-payload';
import { authSignInValidator } from 'modules/auth/domain/validators/auth-sign-in.validator';
import SignInForm from '../components/sign-in-form';
import AuthLayout from '../layouts';

type SportTypeOption = SelectOption<SportType>;

type SignUpFormValues = {
  firstName: string;
  lastName: string;
  middleName?: string;
  phone: string;
  sportType: SportTypeOption | null;
  tournamentName: string;
};

const SignUpPage = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const dispatch = useDispatch<AppDispatch>();
  const isSignInLoading = useSelector(authStoreLoadingSelector);
  const stateErrors = useSelector(authStoreErrorsSelector);
  const isAuthCodeRequested = useSelector(authStoreCodeRequestedSelector);
  const isAuthChecking = useSelector(authStoreCheckingSelector);

  const [username, setUsername] = useState('');

  const sportTypeOptions = useMemo<SportTypeOption[]>(
    () =>
      Object.values(SportType).map((value) => ({
        value,
        label: _.capitalize(t(SPORT_TYPE_LABELS[value])),
      })),
    [t],
  );

  const errors = useMemo(() => {
    if (!stateErrors) {
      return {};
    }

    return Object.keys(stateErrors as { [key: string]: string }).reduce<Record<string, string>>((result, key) => {
      const serverMessage = String((stateErrors as Record<string, unknown>)[key]);
      const message = getSignUpErrorMessage(key, serverMessage);
      if (!message) console.error(`sign-up: no translation for "${key}":`, serverMessage);
      return { ...result, [key]: t(message ?? SIGN_UP_UNKNOWN_FIELD_ERROR) };
    }, {});
  }, [stateErrors, t]);

  // `async` — чтобы промис был и у ранней ветки: иначе Formik не снимет `isSubmitting` и кнопка застрянет.
  const onSignUp = useCallback(
    async (values: SignUpFormValues) => {
      if (!values.sportType) {
        return;
      }
      const payload: AuthSignUpPayload = {
        firstName: values.firstName,
        lastName: values.lastName,
        middleName: values.middleName,
        phone: values.phone,
        sportType: values.sportType.value,
        tournamentName: values.tournamentName,
      };
      setUsername(payload.phone);
      // Промис — наружу: по нему Formik держит кнопку запертой до ответа.
      // Текст бэкенда английский — на экран не идёт: заголовок и причина по коду (нет сети и т. п.).
      return dispatch(authSignUp(payload))
        .unwrap()
        .catch((rejected) => toastApiError(rejected, msg`Не удалось зарегистрироваться`));
    },
    [dispatch, toastApiError],
  );

  // Пришли от AI-клиента (MCP): после регистрации и входа — на согласие с теми же параметрами.
  const { search } = useLocation();
  const oauthSearch = isOauthAuthorizeSearch(search) ? search : null;
  const to = oauthSearch ? oauthConsentPath(oauthSearch) : undefined;

  const onSignIn = useCallback((data: AuthSignInPayload) => dispatch(authSignIn({ ...data, to })), [dispatch, to]);

  return (
    <AuthLayout
      logoTo={Pages.SIGN_IN}
      showPrivacyPolicy
      formContainerProps={{ style: { padding: 30 } }}
      title={t`Регистрация организатора`}
      action={{
        title: t`Уже есть аккаунт?`,
        linkLabel: t`Войти`,
        link: `${Pages.SIGN_IN}${oauthSearch ?? ''}`,
      }}
    >
      {isAuthCodeRequested || isSignInLoading || isAuthChecking ? (
        <SignInForm
          initialValues={{ username, password: '' }}
          onSubmit={onSignIn}
          // Проверка сессии после входа — запрос идёт не из сабмита; сам вход запирает Formik.
          isLoading={isAuthChecking}
          validationSchema={authSignInValidator()}
        />
      ) : (
        <Formik<SignUpFormValues>
          enableReinitialize={true}
          initialValues={{
            firstName: '',
            lastName: '',
            middleName: '',
            phone: '',
            sportType: null,
            tournamentName: '',
          }}
          initialErrors={errors}
          onSubmit={onSignUp}
          validateOnBlur={true}
          validateOnChange={true}
          validationSchema={authSignUpValidator()}
        >
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <UDFormInput name="lastName" placeholder={t`Фамилия`} containerProps={{ style: { marginBottom: 12 } }} errorVisible />
              <UDFormInput name="firstName" placeholder={t`Имя`} containerProps={{ style: { marginBottom: 12 } }} errorVisible />
              <UDFormInput name="middleName" placeholder={t`Отчество`} containerProps={{ style: { marginBottom: 12 } }} errorVisible />
              <UDInputPhone
                name="phone"
                placeholder={t`Номер телефона`}
                containerProps={{ style: { marginBottom: 12 } }}
                value={formProps.values.phone}
                errorVisible
              />
              <UDFormSelect
                name="sportType"
                placeholder={t`Вид спорта`}
                isMulti={false}
                options={sportTypeOptions}
                containerProps={{ style: { marginBottom: 12 } }}
                errorVisible
              />
              <UDFormInput
                name="tournamentName"
                placeholder={t`Название турнира`}
                containerProps={{ style: { marginBottom: 18 } }}
                errorVisible
              />
              <UDButton variant="primary" type="submit" className="w-full" disabled={!formProps.isValid || !formProps.dirty}>
                {t`Создать аккаунт`}
              </UDButton>
            </form>
          )}
        </Formik>
      )}
    </AuthLayout>
  );
};

export default SignUpPage;
